import { Conversation, Order, OrderStatus, OrderType } from '@dazzle/api-client';
import React, { createContext, PropsWithChildren, useContext, useRef, useState } from 'react';

import useLocalStorage from 'Common/hooks/useLocalStorage';
import {
  conversationGetInternal,
  conversationSendAsDazzle,
  orderGet,
  orderList,
  orderPlace,
  orderUpdate
} from 'Common/api/DazzleInternalService';

interface Concierge {
  name: string;
  email: string;
}

export const CONCIERGE_ALL = '__ALL__';
export const CONCIERGE_UNASSIGNED = '__UNASSIGNED__';

export enum OrderStatusFilter {
  Open = 'open',
  Closed = 'closed'
}

export const OrderStatusFilterNames: { [key in OrderStatusFilter]: string } = {
  [OrderStatusFilter.Open]: 'Open Orders',
  [OrderStatusFilter.Closed]: 'Closed Orders',
};

export const OrderTypeFilterNames: { [key in OrderType]: string } = {
  [OrderType.Product]: 'Product',
  [OrderType.Service]: 'Service',
  [OrderType.Concierge]: 'Concierge',
};

interface IUOrdersContext {
  closedOrders: Order[];
  conciergeFilter?: string;
  concierges: Concierge[];
  firstPage: boolean;
  moreOrders: boolean;
  orders?: Order[];
  orderStatusFilter: OrderStatusFilter;
  orderTypeFilter: OrderType;

  fetchOrder: (orderId: string) => Promise<Order | undefined>;
  fetchOrders: (force?: boolean) => Promise<void>;
  fetchConversation: (conversationId: string) => Promise<Conversation | undefined>;
  getOrder: (orderId: string) => Order | undefined;
  nextPage: () => Promise<void>;
  placeOrder: (order: Order) => Promise<void>;
  previousPage: () => Promise<void>;
  replyToUser: (orderId: string, conversationId: string, content: string, imageUrl?: string) => Promise<void>;
  setConciergeFilter: (concierge: string) => void;
  setOrderStatusFilter: (orderStatusFilter: OrderStatusFilter) => void;
  setOrderTypeFilter: (orderTypeFilter: OrderType) => void;
  setOrders: (orders: Order[]) => void;
  updateClosedOrders: (orders: Order[]) => void;
  updateOrder: (order: Order) => Promise<void>;
}

const OrdersContext = createContext<IUOrdersContext | null>(null);

export const OrdersContextProvider = ({ children }: PropsWithChildren<object>) => {
  const [orders, setOrders] = useState<Order[] | undefined>(undefined);
  const [closedOrders, setClosedOrders] = useState<Order[]>([]);
  const [moreOrders, setMoreOrders] = useState(false);
  const [pageTokens, setPageTokens] = useState<string[]>([]);
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>(undefined);
  const [orderStatusFilter, setOrderStatusFilter] = useLocalStorage<OrderStatusFilter>('tableOrderStatus',
    OrderStatusFilter.Open);
  const [orderTypeFilter, setOrderTypeFilter] = useLocalStorage<OrderType>('tableOrderType',
    OrderType.Product);
  const [conciergeFilter, setConciergeFilter] = useLocalStorage<string>('tableConcierge', CONCIERGE_ALL);
  const lastFetchedAt = useRef(0);

  const [concierges] = useState<Concierge[]>([
    {
      name: 'Ali',
      email: 'ali@dazzle.ai'
    },
    {
      name: 'Andy',
      email: 'andy@dazzle.ai'
    },
    {
      name: 'Bella',
      email: 'bella@dazzle.ai'
    },
    {
      name: 'Hemanth',
      email: 'hemanth@dazzle.ai'
    },
    {
      name: 'Jay',
      email: 'jay@dazzle.ai'
    },
    {
      name: 'JMC',
      email: 'jmc@dazzle.ai'
    },
    {
      name: 'Laura',
      email: 'laura@dazzle.ai'
    },
    {
      name: 'Marissa',
      email: 'marissa@dazzle.ai'
    },
    {
      name: 'Monique',
      email: 'monique@dazzle.ai'
    },
    {
      name: 'Olli',
      email: 'olli@dazzle.ai'
    },
    {
      name: 'Shannon',
      email: 'shannon@dazzle.ai'
    },
    {
      name: 'Vishal',
      email: 'vishal@dazzle.ai'
    }
  ]);

  const fetchOrders = async (force?: boolean) => {
    if (!lastFetchedAt.current || force) {
      try {
        return await fetchOrdersInternal({ force, orderStatusFilter, orderTypeFilter, conciergeFilter });
      } catch (e) {
        lastFetchedAt.current = 0;
        throw e;
      }
    }
  };

  const fetchOrdersInternal = async ({ force, orderStatusFilter, orderTypeFilter, conciergeFilter, pageToken }: {
    force?: boolean;
    orderStatusFilter: OrderStatusFilter;
    orderTypeFilter: OrderType;
    conciergeFilter: string;
    pageToken?: string;
  }) => {
    if (force || orders === undefined) {
      let statusFilters: OrderStatus[];
      if (orderStatusFilter === OrderStatusFilter.Closed) {
        statusFilters = [OrderStatus.OrderClosed];
      } else {
        statusFilters = Object.values(OrderStatus)
          .filter(status => {
            return status !== OrderStatus.SuggestionPending && status !== OrderStatus.OrderClosed;
          });
      }

      let assignedToFilter: string | undefined | null = undefined;
      if (conciergeFilter === CONCIERGE_UNASSIGNED) {
        assignedToFilter = null;
      } else if (conciergeFilter !== CONCIERGE_ALL) {
        assignedToFilter = conciergeFilter;
      }

      setOrders(undefined);

      const iFetchedAt = lastFetchedAt.current = Date.now();
      const response = await orderList({
        statusFilters,
        assignedToFilter,
        typeFilter: orderTypeFilter,
        pageToken,
        limit: 20
      });
      if (iFetchedAt !== lastFetchedAt.current) {
        return;
      }
      const orders = response?.orders ?? [];

      setOrders(
        orders
      );
      setMoreOrders(response?.has_more ?? false);
      if (response?.page_token) {
        pageTokens.push(response.page_token)
        setPageTokens(
          [...pageTokens]
        );
      }
      setCurrentPageToken(pageToken);
    }
  };

  const setOrderStatusFilterInternal = async (orderStatusFilter: OrderStatusFilter) => {
    setOrderStatusFilter(orderStatusFilter);
    await fetchOrdersInternal({
      force: true,
      orderStatusFilter,
      orderTypeFilter,
      conciergeFilter,
    });
  };

  const setOrderTypeFilterInternal = async (orderTypeFilter: OrderType) => {
    setOrderTypeFilter(orderTypeFilter);
    await fetchOrdersInternal({
      force: true,
      orderStatusFilter,
      orderTypeFilter,
      conciergeFilter,
    });
  };

  const setConciergeFilterInternal = async (conciergeFilter: string) => {
    setConciergeFilter(conciergeFilter);
    await fetchOrdersInternal({
      force: true,
      orderStatusFilter,
      orderTypeFilter,
      conciergeFilter,
    });
  };

  const fetchConversation = async (conversationId: string) => {
    return (await conversationGetInternal(conversationId))?.conversation;
  };

  const replyToUser = async (orderId: string, conversationId: string, content: string, imageUrl?: string) => {
    await conversationSendAsDazzle({
      orderId,
      content,
      conversationId,
      imageUrl
    });
  };

  const getOrder = (orderId: string) => {
    return orders?.find(order => order.order_id === orderId);
  };

  const fetchOrder = async (orderId: string) => {
    const response = await orderGet(orderId);
    if (!response) {
      return undefined;
    }

    const newOrder = response.order;
    if (orders) {
      const existingIndex = orders.findIndex(order => order.order_id === orderId);
      if (existingIndex !== -1) {
        const newOrders = [...orders];
        newOrders[existingIndex] = newOrder;
        setOrders(newOrders);
      }
    }

    return newOrder;
  };

  const nextPage = async () => {
    await fetchOrdersInternal({
      force: true,
      orderStatusFilter,
      orderTypeFilter,
      conciergeFilter,
      pageToken: pageTokens[pageTokens.length - 1]
    });
  };

  const previousPage = async () => {
    pageTokens.pop();
    pageTokens.pop();
    const pageToken = pageTokens[pageTokens.length - 1];
    await fetchOrdersInternal({
      force: true,
      orderStatusFilter,
      orderTypeFilter,
      conciergeFilter,
      pageToken
    })
  };

  const updateInMemoryOrder = (newOrder: Order) => {
    if (!orders) {
      return;
    }
    newOrder.last_updated_ts = new Date().toISOString();
    const latest: Order[] = [];
    for (const order of orders) {
      if (order.order_id === newOrder.order_id) {
        latest.push(newOrder);
      } else {
        latest.push(order);
      }
    }
    setOrders(latest);
  };

  const updateOrder = async (newOrder: Order) => {
    await orderUpdate(newOrder);

    updateInMemoryOrder(newOrder);
  };

  const placeOrder = async (order: Order) => {
    const newOrder: Order = {
      ...order,
      status: OrderStatus.PaymentCompleted
    };
    try {
      await orderPlace(newOrder);
    } catch (e) {
      newOrder.status = OrderStatus.PaymentFailed;
      updateInMemoryOrder(newOrder);
      throw e;
    }

    updateInMemoryOrder(newOrder);
  };

  const updateClosedOrders = (_closedOrders: Order[]) => {
    setClosedOrders(_closedOrders);
  };

  const context = {
    closedOrders,
    conciergeFilter,
    concierges,
    firstPage: currentPageToken === undefined,
    moreOrders,
    orders,
    orderStatusFilter,
    orderTypeFilter,

    fetchOrder,
    fetchOrders,
    fetchConversation,
    getOrder,
    nextPage,
    placeOrder,
    previousPage,
    replyToUser,
    setConciergeFilter: setConciergeFilterInternal,
    setOrderStatusFilter: setOrderStatusFilterInternal,
    setOrderTypeFilter: setOrderTypeFilterInternal,
    setOrders,
    updateClosedOrders,
    updateOrder,
  };

  return <OrdersContext.Provider value={context}>
    {children}
  </OrdersContext.Provider>;
};

export const useOrdersContext = () => {
  const context = useContext(OrdersContext);
  if (context === null) {
    throw new Error('useOrdersContext must be used within a OrdersContextProvider');
  }
  return context;
};
